import React, { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Archive,
  Settings,
  LogOut,
  Moon,
  Sun,
  Shield,
  CalendarDays,
  Newspaper,
  ClipboardList,
  ClipboardCheck,
  PenLine,
  HelpCircle,
} from 'lucide-react';
import { ORG_NAME } from '../constants';
import { supabase } from '../services/supabaseClient';
import { Button } from './ui/button';

interface SidebarProps {
  currentView: string;
  onChangeView: (view: string) => void;
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentView, onChangeView, isDarkMode, toggleTheme }) => {
  const [profile, setProfile] = useState<{ name: string; role: string; avatar: string } | null>(null);

  useEffect(() => {
    const getProfile = async () => {
      // 1. Get the authenticated user's ID
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        // 2. Fetch the corresponding record from the 'profiles' table
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error.message);
        }

        if (data) {
          // 3. Map the database columns to the sidebar state
          setProfile({
            name: data.full_name || user.email || 'User',
            role: data.role || 'journalist',
            avatar: data.avatar_url || `https://ui-avatars.com/api/?name=${user.email}`,
          });
        } else {
          // Fallback if no profile record exists yet
          setProfile({
            name: user.email || 'User',
            role: 'journalist',
            avatar: `https://ui-avatars.com/api/?name=${user.email}`,
          });
        }
      }
    };
    getProfile();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  // Simplified navigation for journalists - only Writing Tool, Admin (for admins), and Settings
  const navItems = [
    { id: 'writing_tool', label: 'Writing Tool', icon: PenLine },
    { id: 'editor_desk', label: 'Editor Desk', icon: ClipboardCheck, editorOnly: true },
    { id: 'all_stories', label: 'All Stories', icon: Newspaper, adminOnly: true },
    { id: 'admin', label: 'Admin', icon: Shield, adminOnly: true },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'help', label: 'Help', icon: HelpCircle },
  ];

  return (
    <div className="w-64 bg-zinc-900 dark:bg-zinc-950 text-zinc-100 flex flex-col h-full border-r border-zinc-800 transition-colors duration-200">
      <div className="p-6">
        <h1 className="text-xl font-bold tracking-tight text-white">
          {ORG_NAME} <span className="font-light text-zinc-400">OS</span>
        </h1>
        <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">Editorial Platform</p>
      </div>

      <div className="px-3 py-2 flex-1">
        <p className="px-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
          Navigation
        </p>
        <nav className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;

            // Admins and superusers see everything - skip role checks
            const isAdmin = profile?.role === 'admin' || profile?.role === 'superuser';

            if (isAdmin) {
              // Admin sees all items - no filtering
            } else {
              // Only show Admin tab to admins
              if ((item as any).adminOnly) return null;

              // Only show editor-only items to editors, producers, supervisors
              const editorRoles = ['editor', 'senior_editor', 'producer', 'supervisor'];
              if ((item as any).editorOnly && !editorRoles.includes(profile?.role || '')) return null;
            }

            return (
              <Button
                key={item.id}
                onClick={() => onChangeView(item.id)}
                variant={isActive ? "default" : "ghost"}
                className={`w-full justify-start ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-900/10 hover:bg-blue-700'
                    : ''
                }`}
              >
                <Icon
                  className={`mr-3 h-5 w-5 ${isActive ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-300'}`}
                />
                {item.label}
              </Button>
            );
          })}
        </nav>
      </div>

      <div className="p-4 border-t border-zinc-800">
        {/* Theme Toggle */}
        <Button
          onClick={toggleTheme}
          variant="ghost"
          className="w-full justify-between px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/50 mb-4"
        >
          <span className="flex items-center">
            {isDarkMode ? <Moon className="h-4 w-4 mr-2" /> : <Sun className="h-4 w-4 mr-2" />}
            {isDarkMode ? 'Dark Mode' : 'Light Mode'}
          </span>
          <div
            className={`w-8 h-4 rounded-full relative transition-colors ${isDarkMode ? 'bg-blue-600' : 'bg-zinc-600'}`}
          >
            <div
              className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform duration-200 ${isDarkMode ? 'left-4.5 translate-x-4' : 'left-0.5'}`}
            />
          </div>
        </Button>

        {profile && (
          <div className="flex items-center pt-2">
            <div className="flex-shrink-0">
              <div className="h-9 w-9 rounded-full bg-zinc-700 ring-2 ring-zinc-800 flex items-center justify-center overflow-hidden">
                <img src={profile.avatar} alt="User" className="h-full w-full object-cover opacity-80" />
              </div>
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium text-white truncate">{profile.name}</p>
              <p className="text-xs text-zinc-500 capitalize truncate">{profile.role.replace('_', ' ')}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="icon"
              className="ml-auto text-zinc-500 hover:text-white"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
